import { observable, action, computed, runInAction } from 'mobx';
import UserAPI from 'api/user';
import { getDefaultLocale } from 'ui/i18n';
import task from './helpers/task';
import Cart from './Cart';

export class App {
	@observable current = null;
	@observable impersonated = null;
	@observable locale = getDefaultLocale();
	@observable locations = [];
	@observable error = null;

	constructor() {
		this.fetchUser();
		//event needed for activating loader before redirecting to SSO
		window.addEventListener('beforeunload', () => this.setInLoading());
	}

	@computed
	get applicationLoading() {
		return (
			this.fetchUser.state.loading ||
			this.impersonate.state.loading ||
			this.reverseImpersonation.state.loading
		);
	}

	@computed
	get user() {
		return this.isImpersonated ? this.impersonated : this.current;
	}

	@computed
	get isImpersonated() {
		return Boolean(this.impersonated);
	}

	@computed
	get isOnHoldOrTerminated() {
		return this.user.status === 'OnHold' || this.user.status === 'Terminated';
	}

	@computed
	get isCompanyStore() {
		return this.user.user_types.includes('CompanyStore');
	}

	@computed
	get isfromCanada() {
		return this.current.country.short_name === 'CA';
	}

	@computed
	get isfromUS() {
		return this.current.country.short_name === 'US';
	}

	@computed
	get isfromAUSorNZL() {
		return (
			this.user.country.short_name === 'AU' ||
			this.user.country.short_name === 'NZ'
		);
	}

	@computed
	get userRights() {
		return this.user.rights;
	}

	@action
	setInLoading() {
		this.fetchUser.state.setInLoading();
	}

	@action
	setLocale(locale) {
		this.locale = locale;
		sessionStorage.setItem('currentLocale', locale);
		window.location.reload();
	}

	@action
	saveLocation(pathname) {
		this.locations.unshift(pathname);
	}

	@task
	fetchUser() {
		return UserAPI.fetchUser().then(users =>
			runInAction(() => {
				this.current = users.current;
				this.impersonated = users.impersonated;
				this.locale = getDefaultLocale(this.user.country.short_name);
				Cart.replaceCart(this.user.cart, false);

				return this.user;
			})
		);
	}

	@task
	searchUserToImpersonate(id) {
		this.userToImpersonate = null;

		return UserAPI.searchUser(id).then(user =>
			runInAction(() => {
				this.userToImpersonate = user;
				return this.userToImpersonate;
			})
		);
	}

	@action
	resetUserToImpersonate() {
		this.searchUserToImpersonate.state.setInPending();
	}

	@task
	impersonate() {
		if (!this.userToImpersonate) {
			return false;
		}
		const userIdentification = this.userToImpersonate.bpnumber
			? this.userToImpersonate.bpnumber
			: this.userToImpersonate.dnumber;

		return UserAPI.impersonate(userIdentification).then(() =>
			runInAction(() => {
				this.fetchUser.state.setInLoading(); // keep the loading on
				this.reload();
			})
		);
	}

	@task
	reverseImpersonation() {
		this.locale = getDefaultLocale(this.current.country.short_name);
		return UserAPI.reverseImpersonation().then(
			() =>
				runInAction(() => {
					this.fetchUser.state.setInLoading(); // keep the loading on
					this.reload();
				}),
			() => {
				runInAction(() => {
					this.reload();
					window.location.reload();
				});
			}
		);
	}

	reload() {
		window.location.pathname = '/';
	}
}

export default new App();



// WEBPACK FOOTER //
// ./src/state/App.js